/**
 * PF v2 file hub — shared document projection.
 * ============================================================================
 *
 * The read model every plain-document upload scope exposes: the uploaded GCS
 * filenames. Lifted verbatim (no logic change) out of `powerOfAttorney.tsx` so
 * both PoA (Wave 1) and the AdditionalInformation cert scopes (Wave 2) share
 * ONE projection without a cross-kind deep-import. `powerOfAttorney.tsx`
 * re-exports these (aliasing `DocProjection` as `PoaProjection`) so its public
 * surface is unchanged.
 *
 * All document scopes carry no derivative artifact, so the projection is a pure
 * SUCCESS+UPLOAD filename map — a single-doc scope caps at one filename, a
 * multi-doc scope returns all of them.
 */

import { FileAction, FileState, type FileAPIEntry } from 'components/common/FileAPI'

import type { FileHubBaseMetadata } from '../fileHub.types'

/** The read model a document scope exposes: the uploaded GCS filenames. */
export interface DocProjection {
  filenames: string[]
}

/**
 * Filenames of fully-uploaded entries. Filters SUCCESS + UPLOAD/RE_UPLOAD (which
 * also matches seeded markers — SUCCESS/UPLOAD) and maps to `gcFilename ?? id`
 * (the BE storage key, = legacy `fileIdOf`). No `derivativeReady` gate — plain
 * document uploads stamp it true on settle and seeds set it true, so SUCCESS +
 * UPLOAD is sufficient and matches legacy.
 */
const asFilenames = (entries: Array<FileAPIEntry<FileHubBaseMetadata>>): string[] =>
  entries
    .filter(
      (e) =>
        e.state === FileState.SUCCESS &&
        (e.action === FileAction.UPLOAD || e.action === FileAction.RE_UPLOAD)
    )
    .map((e) => e.gcFilename ?? e.id)

/** Single-doc scopes (PoA main/reps, all four cert scopes): at most one filename. */
export const projectSingleDoc = (entries: Array<FileAPIEntry<FileHubBaseMetadata>>): DocProjection => ({
  filenames: asFilenames(entries).slice(0, 1),
})

/** Multi-doc scope (PoA "other"): all uploaded filenames (BE caps at ~15). */
export const projectMultiDoc = (entries: Array<FileAPIEntry<FileHubBaseMetadata>>): DocProjection => ({
  filenames: asFilenames(entries),
})
